// Components
import AddToFavoritesButton from 'components/track/addtofavoritesbutton'
import AddToCollectionButton from 'components/track/addtocollectionbutton'
import DownloadButton from 'components/base/downloadbutton'
import ShareButton from 'components/base/sharebutton'
import { ContextMenu } from 'kit'
import {
  IconAddToFavoritesOutline,
  IconAddToCollectionOutline,
  IconAddToFavoritesFilled,
  IconAddToCollectionFilled,
  IconArrowNegative,
} from '@tracklib/kit'

// Hooks
import { useActions } from 'modules/audio/hooks'
import { useIsInFavoritesCollection, useIsInAnyCollection } from 'modules/collections/hooks'
import { useIsFreeTrack } from 'modules/price/hooks'
import { useTrackIsUnpublished } from 'modules/entities/hooks'

// Types
import { Loop } from 'types'

import styles from './buttons.css'

type ButtonMenuProps = {
  trackId: number
  loop: Loop | undefined
  origin?: string
  className?: string
}

export const ButtonMenu = ({ trackId, loop, origin, className }: ButtonMenuProps): JSX.Element => {
  const { handleAddToCollection, handleAddToFavorites, handleDownload } = useActions()
  const isInFavorites = useIsInFavoritesCollection(trackId, loop)
  const isInAnyCollection = useIsInAnyCollection(trackId, loop)
  const type = loop ? 'loop' : 'track'
  const isFreeTrack = useIsFreeTrack(trackId)
  const isUnpublished = useTrackIsUnpublished(trackId)

  return (
    <ContextMenu origin={origin} className={className}>
      <ContextMenu.Item
        text={isInFavorites ? `Remove ${type} from favorites` : `Add ${type} to favorites`}
        icon={
          isInFavorites ? <IconAddToFavoritesFilled className={styles.fill} /> : <IconAddToFavoritesOutline className={styles.outline} />
        }
        onClick={() => handleAddToFavorites(trackId, loop)}
      />
      <ContextMenu.Item
        text="Add/remove from collection"
        icon={
          isInAnyCollection ? <IconAddToCollectionFilled className={styles.fill} /> : <IconAddToCollectionOutline className={styles.fill} />
        }
        onClick={() => handleAddToCollection(trackId, loop)}
      />
      <ContextMenu.Item
        filled
        text={`Download ${type} ${isFreeTrack ? '(Free)' : ''}`}
        icon={<IconArrowNegative className={styles.arrow} />}
        onClick={() => handleDownload(trackId, loop)}
        disabled={isUnpublished}
      />
    </ContextMenu>
  )
}

type ButtonsProps = {
  trackId: number
  loop: Loop | undefined
  shareUrl: string
  mode?: string
}

const Buttons = ({ trackId, loop, shareUrl, mode }: ButtonsProps): JSX.Element => {
  if (mode === 'extraCompact') {
    return (
      <div className={styles.extraCompactWrapper}>
        <ButtonMenu trackId={trackId} loop={loop} />
        <DownloadButton trackId={trackId} loop={loop} extraCompact />
      </div>
    )
  }
  return (
    <div className={styles.wrapper}>
      <AddToFavoritesButton trackId={trackId} loop={loop} />
      <AddToCollectionButton trackId={trackId} loop={loop} />
      <ShareButton url={shareUrl} isLoop={!!loop} />
      <DownloadButton trackId={trackId} loop={loop} />
      <ButtonMenu trackId={trackId} loop={loop} />
    </div>
  )
}

export default Buttons
